import { api } from '@/shared/services/api';
import {
  IOrganizationRepository,
  CreateOrganizationData,
  UpdateOrganizationData,
  CreateUserData,
  UpdateUserData,
  OrganizationListResponse,
  MyOrganizationResponse,
  MyOrganizationUser,
  NotificationTask,
} from '../../domain/repositories/IOrganizationRepository';
import {
  Organization,
  PlanResource,
  ScheduledPlanChange,
} from '../../domain/entities/Organization';
import { PaymentMethodSnapshot } from '../../domain/entities/PaymentMethodSnapshot';

interface OrgApiItem {
  id: string;
  name: string;
  slug: string;
  status: string;
  planId: string;
  planName: string;
  createdDate: string;
  planStartDate: string;
  planEndDate: string;
  domains: string[];
  aggregates: { connections: number; users: number; tasks: number; items: number };
  limits: { users: number; tasks: number; connections: number; workspaces: number } | null;
  paymentMethod?: 'card' | 'transfer';
  holdedContactId?: string;
  sendTrialEmails?: boolean;
}

const stripQuotes = (value: string): string => value.replace(/^"+|"+$/g, '');

/**
 * OrganizationHttpRepository (Adapter)
 *
 * @param apiPrefix - Prefijo de la ruta según el rol:
 *   - '/admin' para superadmin → /v2/admin/organizations
 *   - '/organizations' para admin/backupmanager (getMe() no lo usa: llama al
 *     literal absoluto /v2/organizations/me, único método sin prefijo por rol — D10)
 */
export class OrganizationHttpRepository implements IOrganizationRepository {
  constructor(private readonly apiPrefix: string = '/admin') {}

  async list(): Promise<OrganizationListResponse> {
    const response = await api.get<{
      data: OrgApiItem[];
      totals: OrganizationListResponse['totals'];
    }>(`/v2${this.apiPrefix}/organizations`);
    const organizations = response.data.data.map(item =>
      Organization.fromPrimitives({
        id: item.id,
        name: item.name,
        slug: item.slug,
        status: item.status,
        planId: item.planId,
        planName: item.planName,
        createdDate: item.createdDate,
        planStartDate: stripQuotes(item.planStartDate ?? ''),
        planEndDate: stripQuotes(item.planEndDate ?? ''),
        domains: item.domains ?? [],
        aggregates: item.aggregates,
        limits: item.limits,
        paymentMethod: item.paymentMethod,
        holdedContactId: item.holdedContactId,
        sendTrialEmails: item.sendTrialEmails,
      })
    );
    return { organizations, totals: response.data.totals };
  }

  async getMe(): Promise<MyOrganizationResponse> {
    const response = await api.get<{
      id: string;
      name: string;
      slug: string;
      status: string;
      plan: {
        id: string;
        name: string;
        startDate: string;
        endDate: string;
        limits: { users: number; tasks: number; connections: number; workspaces: number };
      } | null;
      usage: { connections: number; users: number; tasks: number; items: number };
      domains: string[];
      trialExtendedOnce?: boolean;
      hasStripeCustomer?: boolean;
      inactivityReason?: string | null;
      hasActiveSubscription?: boolean;
      scheduledPlanChange?: ScheduledPlanChange | null;
      scheduledPlanChangeFits?: boolean | null;
      scheduledPlanChangeBlockedBy?: PlanResource[] | null;
      autoRenew?: boolean;
      paymentMethod?: 'card' | 'transfer';
      holdedContactId?: string;
      settlementCurrency?: string | null;
      cancellationRequestedAt?: string | null;
      users: MyOrganizationUser[];
      connections: {
        id: string;
        name: string;
        status: boolean;
        timezone: string;
        region: 'US' | 'EU';
        notificationLanguage?: 'es' | 'en';
        numTasks: number;
        numUsers: number;
        domain: string;
        email?: string;
      }[];
    }>('/v2/organizations/me');
    // numUsers: todos los usuarios del connection, sin filtrar por rol — este
    // endpoint NO cambia (design.md D7 de h7-organizations-core-to-postgres).

    const d = response.data;
    const organization = Organization.fromPrimitives({
      id: d.id,
      name: d.name,
      slug: d.slug,
      status: d.status,
      planId: d.plan?.id ?? '',
      planName: d.plan?.name ?? '',
      createdDate: '',
      planStartDate: stripQuotes(d.plan?.startDate ?? ''),
      planEndDate: stripQuotes(d.plan?.endDate ?? ''),
      domains: d.domains ?? [],
      aggregates: d.usage,
      limits: d.plan?.limits ?? null,
      trialExtendedOnce: d.trialExtendedOnce ?? false,
      hasStripeCustomer: d.hasStripeCustomer ?? false,
      inactivityReason: d.inactivityReason ?? null,
      hasActiveSubscription: d.hasActiveSubscription ?? false,
      scheduledPlanChange: d.scheduledPlanChange ?? null,
      scheduledPlanChangeFits: d.scheduledPlanChangeFits ?? null,
      scheduledPlanChangeBlockedBy: d.scheduledPlanChangeBlockedBy ?? null,
      autoRenew: d.autoRenew ?? false,
      paymentMethod: d.paymentMethod ?? 'card',
      holdedContactId: d.holdedContactId ?? '',
      settlementCurrency: d.settlementCurrency ?? null,
      cancellationRequestedAt: d.cancellationRequestedAt ?? null,
    });

    return { organization, users: d.users, connections: d.connections ?? [] };
  }

  async getOrgDetail(orgId: string): Promise<MyOrganizationResponse> {
    const response = await api.get<{
      id: string;
      name: string;
      slug: string;
      status: string;
      plan: {
        id: string;
        name: string;
        startDate: string;
        endDate: string;
        limits: { users: number; tasks: number; connections: number; workspaces: number };
      } | null;
      usage: { connections: number; users: number; tasks: number; items: number };
      domains: string[];
      users: MyOrganizationUser[];
      // numBackupManagers: solo los BackupManager asignados al connection —
      // numUsers ya NO existe en este endpoint (design.md D7 de
      // h7-organizations-core-to-postgres).
      connections: {
        id: string;
        name: string;
        status: boolean;
        timezone: string;
        region: 'US' | 'EU';
        notificationLanguage?: 'es' | 'en';
        numTasks: number;
        numBackupManagers: number;
        domain: string;
        email?: string;
      }[];
    }>(`/v2${this.apiPrefix}/organizations/${orgId}`);

    const d = response.data;
    const organization = Organization.fromPrimitives({
      id: d.id,
      name: d.name,
      slug: d.slug,
      status: d.status,
      planId: d.plan?.id ?? '',
      planName: d.plan?.name ?? '',
      createdDate: '',
      planStartDate: stripQuotes(d.plan?.startDate ?? ''),
      planEndDate: stripQuotes(d.plan?.endDate ?? ''),
      domains: d.domains ?? [],
      aggregates: d.usage,
      limits: d.plan?.limits ?? null,
    });

    return { organization, users: d.users, connections: d.connections ?? [] };
  }

  async create(data: CreateOrganizationData): Promise<{ id: string }> {
    const response = await api.post<{ id: string }>(`/v2${this.apiPrefix}/organizations`, {
      name: data.name,
      slug: data.slug,
      planId: data.planId,
      status: data.status,
      planStartDate: data.planStartDate,
      planEndDate: data.planEndDate,
      domains: data.domains,
      paymentMethod: data.paymentMethod,
      holdedContactId: data.holdedContactId,
      sendTrialEmails: data.sendTrialEmails,
    });
    return response.data;
  }

  async update(id: string, data: UpdateOrganizationData): Promise<void> {
    await api.patch(`/v2${this.apiPrefix}/organizations/${id}`, {
      name: data.name,
      planId: data.planId,
      status: data.status,
      planStartDate: data.planStartDate,
      planEndDate: data.planEndDate,
      domains: data.domains,
      paymentMethod: data.paymentMethod,
      holdedContactId: data.holdedContactId,
      sendTrialEmails: data.sendTrialEmails,
    });
  }

  async createUser(data: CreateUserData): Promise<MyOrganizationUser> {
    const response = await api.post<{
      data: {
        id: string;
        email: string;
        role: string;
        organizationId?: string;
        firebaseUid: string;
        clientIds: string[];
      };
    }>(`/v2${this.apiPrefix}/users/`, {
      name: data.Name,
      surname: data.Surname,
      secondSurname: data.Surname2,
      email: data.Email,
      role: data.Role,
      organizationId: data.Organization_ID,
      clientIds: data.connectionIds ?? [], // D7: traduce al wire aquí, en el punto de serialización.
      notificationLanguage: data.NotificationLanguage ?? 'es',
      clientUrl: window.location.origin,
    });
    const d = response.data.data;
    return {
      id: d.id,
      name: data.Name,
      surname: data.Surname,
      secondSurname: data.Surname2 ?? '',
      email: d.email,
      role: d.role,
      status: 'active',
      lastLogin: null,
      connections: d.clientIds ?? [],
    };
  }

  async updateUser(userId: string, data: UpdateUserData): Promise<void> {
    // R4: Whitelist — solo campos permitidos, mapeados a camelCase para /v2
    const body: Record<string, unknown> = {};
    if (data.Name !== undefined) body.name = data.Name;
    if (data.Surname !== undefined) body.surname = data.Surname;
    if (data.Surname2 !== undefined) body.secondSurname = data.Surname2;
    if (data.Role !== undefined) body.role = data.Role;
    if (data.status !== undefined) body.status = data.status === 'active';
    if (data.connectionIds !== undefined) body.clientIds = data.connectionIds;

    await api.patch(`/v2${this.apiPrefix}/users/${userId}`, body);
  }

  async updateUserStatus(userId: string, status: 'active' | 'inactive'): Promise<void> {
    await api.patch(`/v2${this.apiPrefix}/users/${userId}/status`, { status });
  }

  async removeUser(
    userId: string,
    reassignments: { taskId: string; newUserId: string }[]
  ): Promise<void> {
    await api.delete(`/v2${this.apiPrefix}/users/${userId}`, { data: { reassignments } });
  }

  async getNotificationTasks(userId: string): Promise<NotificationTask[]> {
    const response = await api.get<{ data: NotificationTask[] }>(
      `/v2${this.apiPrefix}/users/${userId}/notification-tasks`
    );
    return response.data.data;
  }

  async getOrgUsersByOrgId(orgId: string): Promise<MyOrganizationUser[]> {
    const response = await api.get<MyOrganizationUser[]>(
      `/v2${this.apiPrefix}/organizations/${orgId}/users`
    );
    return response.data;
  }

  async cancelScheduledPlanDowngrade(): Promise<void> {
    await api.delete('/v2/organizations/plan-change/scheduled');
  }

  /**
   * Mapea la respuesta de `POST`/`DELETE /v2/organizations/subscription/cancellation-request`
   * (self-service-cancellation-request, D6) a la Entidad de frontend. El backend devuelve ahí la
   * organización en su forma de dominio plana (`OrganizationPrimitives`), distinta del DTO
   * enriquecido de `GET /v2/organizations/me` (sin `plan`/`usage` anidados, sin `limits`,
   * `hasActiveSubscription`, `hasStripeCustomer` ni `trialExtendedOnce` — campos que esa respuesta
   * no necesita calcular). Los campos ausentes se defaultean con el mismo criterio que el resto
   * del repo (`fromPrimitives`) porque `useRequestCancellation` invoca `reload()` justo después
   * del `onSuccess`, que vuelve a hidratar la organización completa vía `getMe()` — esta entidad
   * solo cumple el contrato del método (D6: "el backend SÍ devuelve la entidad") y el status/sello
   * ya vienen correctos para cualquier consumidor que los necesite antes de ese roundtrip.
   */
  private mapCancellationResponse(d: {
    id: string;
    name: string;
    slug: string;
    status: string;
    planId: string;
    planName: string;
    planStartDate: string;
    planEndDate: string;
    domains: string[];
    autoRenew?: boolean;
    sendTrialEmails?: boolean;
    inactivityReason?: string | null;
    scheduledPlanChange?: { targetPlanId: string; effectiveAt: string } | null;
    holdedContactId?: string;
    paymentMethod?: 'card' | 'transfer';
    settlementCurrency?: string | null;
    createdAt?: string;
    cancellationRequestedAt: string | null;
  }): Organization {
    return Organization.fromPrimitives({
      id: d.id,
      name: d.name,
      slug: d.slug,
      status: d.status,
      planId: d.planId,
      planName: d.planName,
      createdDate: d.createdAt ?? '',
      planStartDate: stripQuotes(d.planStartDate ?? ''),
      planEndDate: stripQuotes(d.planEndDate ?? ''),
      domains: d.domains ?? [],
      aggregates: { connections: 0, users: 0, tasks: 0, items: 0 },
      limits: null,
      autoRenew: d.autoRenew ?? false,
      paymentMethod: d.paymentMethod ?? 'card',
      holdedContactId: d.holdedContactId ?? '',
      sendTrialEmails: d.sendTrialEmails ?? true,
      settlementCurrency: d.settlementCurrency ?? null,
      inactivityReason: d.inactivityReason ?? null,
      scheduledPlanChange: d.scheduledPlanChange
        ? {
            targetPlanId: d.scheduledPlanChange.targetPlanId,
            targetPlanName: '',
            effectiveAt: d.scheduledPlanChange.effectiveAt,
          }
        : null,
      cancellationRequestedAt: d.cancellationRequestedAt ?? null,
    });
  }

  async requestCancellation(): Promise<Organization> {
    const response = await api.post<{ data: Parameters<OrganizationHttpRepository['mapCancellationResponse']>[0] }>(
      '/v2/organizations/subscription/cancellation-request'
    );
    return this.mapCancellationResponse(response.data.data);
  }

  async undoCancellationRequest(): Promise<Organization> {
    const response = await api.delete<{ data: Parameters<OrganizationHttpRepository['mapCancellationResponse']>[0] }>(
      '/v2/organizations/subscription/cancellation-request'
    );
    return this.mapCancellationResponse(response.data.data);
  }

  async getPaymentMethod(): Promise<PaymentMethodSnapshot | null> {
    const response = await api.get<{ data: PaymentMethodSnapshot | null }>(
      '/v2/organizations/payment-method'
    );
    return response.data.data;
  }

  async createPaymentMethodSetupIntent(): Promise<{ clientSecret: string }> {
    const response = await api.post<{ data: { clientSecret: string } }>(
      '/v2/organizations/payment-method/setup-intent'
    );
    return response.data.data;
  }

  async setDefaultPaymentMethod(paymentMethodId: string): Promise<void> {
    await api.patch('/v2/organizations/payment-method', { paymentMethodId });
  }
}
